import { x25519 } from '@noble/curves/ed25519';
import { api, decodeVaultBlobResponse } from '../api/client';
import {
  decodeBase64,
  decodeBase64Url,
  encodeBase64Url,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
} from './primitives';
import { resolvePasswordPolicy } from './passwordPoliciesApi';
import { assertPasswordPolicy } from './passwordPolicy';
import {
  decryptVaultPlaintext,
  deriveVaultPassphraseKey,
  encryptVaultPlaintext,
  setStoredVaultVersion,
  type VaultPlaintext,
} from './vault';
import { wrapKey, unwrapKey } from './wrappedKey';

const EXPORT_VERSION = 0x01;
const BUNDLE_VERSION = 0x01;
const RECOVERY_CONTEXT_PREFIX = 'shareweb:recovery:v1';

export interface RecoveryKeyBundle {
  userId: string;
  recoveryGeneration: number;
  recoveryPublicKey: Uint8Array;
  recoveryPrivateKey: Uint8Array;
}

export interface RecoveryServerRecord {
  recoveryPublicKey: Uint8Array;
  recoveryVkWrap: Uint8Array;
  recoveryGeneration: number;
  recoveryActive: boolean;
}

export interface CreateRecoveryPackageResult {
  exportText: string;
  recoveryGeneration: number;
  recoveryPublicKey: Uint8Array;
  recoveryVkWrap: Uint8Array;
}

function hexToBytes(hex: string): Uint8Array {
  return Uint8Array.from(hex.match(/.{1,2}/g)!.map((byte) => Number.parseInt(byte, 16)));
}

export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) {
    throw new Error('invalid_user_id');
  }

  return hexToBytes(hex);
}

export function bytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new Error('invalid_user_id_bytes');
  }

  const hex = Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function writeUint32Be(value: number): Uint8Array {
  const buffer = new Uint8Array(4);
  const view = new DataView(buffer.buffer);
  view.setUint32(0, value >>> 0, false);
  return buffer;
}

function readUint32Be(bytes: Uint8Array, offset: number): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
  return view.getUint32(0, false);
}

export function buildRecoveryContextAad(userId: string, recoveryGeneration: number): string {
  return `${RECOVERY_CONTEXT_PREFIX}|${userId}|${recoveryGeneration}`;
}

export function encodeRecoveryKeyBundle(bundle: RecoveryKeyBundle): Uint8Array {
  const userIdBytes = uuidToBytes(bundle.userId);
  const output = new Uint8Array(1 + 16 + 4 + 32 + 32);
  output[0] = BUNDLE_VERSION;
  output.set(userIdBytes, 1);
  output.set(writeUint32Be(bundle.recoveryGeneration), 17);
  output.set(bundle.recoveryPublicKey, 21);
  output.set(bundle.recoveryPrivateKey, 53);
  return output;
}

export function decodeRecoveryKeyBundle(bytes: Uint8Array): RecoveryKeyBundle {
  if (bytes.length !== 85 || bytes[0] !== BUNDLE_VERSION) {
    throw new Error('invalid_recovery_bundle');
  }

  return {
    userId: bytesToUuid(bytes.slice(1, 17)),
    recoveryGeneration: readUint32Be(bytes, 17),
    recoveryPublicKey: bytes.slice(21, 53),
    recoveryPrivateKey: bytes.slice(53, 85),
  };
}

export function encodeRecoveryExport(bundle: RecoveryKeyBundle): string {
  const inner = encodeRecoveryKeyBundle(bundle);
  const output = new Uint8Array(1 + inner.length);
  output[0] = EXPORT_VERSION;
  output.set(inner, 1);
  return encodeBase64Url(output);
}

export function decodeRecoveryExport(exportText: string): RecoveryKeyBundle {
  const bytes = decodeBase64Url(exportText.trim());
  if (bytes.length !== 86 || bytes[0] !== EXPORT_VERSION) {
    throw new Error('invalid_recovery_export');
  }

  return decodeRecoveryKeyBundle(bytes.slice(1));
}

export function nextRecoveryGeneration(
  recoveryActive: boolean,
  currentGeneration: number,
): number {
  if (!recoveryActive || currentGeneration <= 0) {
    return 1;
  }

  return currentGeneration + 1;
}

export async function createRecoveryPackage(
  userId: string,
  vaultKey: Uint8Array,
  recoveryActive: boolean,
  currentGeneration: number,
): Promise<CreateRecoveryPackageResult> {
  const recoveryGeneration = nextRecoveryGeneration(recoveryActive, currentGeneration);
  const recoveryPrivateKey = x25519.utils.randomPrivateKey();
  const recoveryPublicKey = x25519.getPublicKey(recoveryPrivateKey);
  const contextAad = buildRecoveryContextAad(userId, recoveryGeneration);
  const recoveryVkWrap = await wrapKey(vaultKey, recoveryPublicKey, contextAad);
  const exportText = encodeRecoveryExport({
    userId,
    recoveryGeneration,
    recoveryPublicKey,
    recoveryPrivateKey,
  });

  return {
    exportText,
    recoveryGeneration,
    recoveryPublicKey,
    recoveryVkWrap,
  };
}

export function validateRecoveryImport(
  bundle: RecoveryKeyBundle,
  server: RecoveryServerRecord,
  expectedUserId: string,
): void {
  if (bundle.userId !== expectedUserId) {
    throw new Error('recovery_user_mismatch');
  }

  if (!server.recoveryActive) {
    throw new Error('recovery_not_active');
  }

  if (bundle.recoveryGeneration !== server.recoveryGeneration) {
    throw new Error('recovery_generation_mismatch');
  }

  if (!constantTimeEqual(bundle.recoveryPublicKey, server.recoveryPublicKey)) {
    throw new Error('recovery_public_key_mismatch');
  }
}

export async function recoverVaultKeyFromRecovery(
  bundle: RecoveryKeyBundle,
  server: RecoveryServerRecord,
  expectedUserId: string,
): Promise<Uint8Array> {
  validateRecoveryImport(bundle, server, expectedUserId);
  const contextAad = buildRecoveryContextAad(bundle.userId, bundle.recoveryGeneration);
  return unwrapKey(server.recoveryVkWrap, bundle.recoveryPrivateKey, contextAad);
}

function constantTimeEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) {
    return false;
  }

  let diff = 0;
  for (let i = 0; i < left.length; i += 1) {
    diff |= left[i] ^ right[i];
  }

  return diff === 0;
}

export async function completeRecoveryUnlock(
  userId: string,
  exportText: string,
  newPassphrase: string,
): Promise<{ vaultKey: Uint8Array; userPrivateKey: Uint8Array; plaintext: VaultPlaintext }> {
  const bundle = decodeRecoveryExport(exportText);
  const serverResponse = await api.getRecoveryPackage();
  const server: RecoveryServerRecord = {
    recoveryPublicKey: decodeBase64(serverResponse.recoveryPublicKey),
    recoveryVkWrap: decodeBase64(serverResponse.recoveryVkWrap),
    recoveryGeneration: serverResponse.recoveryGeneration,
    recoveryActive: serverResponse.recoveryActive,
  };

  const vaultKey = await recoverVaultKeyFromRecovery(bundle, server, userId);
  const blobResponse = await api.getVaultBlob();
  const blob = decodeVaultBlobResponse(blobResponse);
  const plaintext = await decryptVaultPlaintext(vaultKey, blob.vaultBlob);

  if (plaintext.vaultVersion !== blob.vaultVersion) {
    throw new Error('vault_version_mismatch');
  }

  if (typeof plaintext.userPriv !== 'string' || plaintext.userPriv.length === 0) {
    throw new Error('vault_missing_user_priv');
  }

  const policy = await resolvePasswordPolicy('vaultPassphrase');
  assertPasswordPolicy(newPassphrase, policy);

  const nextVersion = plaintext.vaultVersion + 1;
  plaintext.vaultVersion = nextVersion;
  const passphraseKey = await deriveVaultPassphraseKey(newPassphrase, blob.vaultSalt);
  const vkWrapAesKey = await importAesKey(passphraseKey);
  const vkWrapPassphrase = packEncryptedBlob(await encryptAesGcm(vkWrapAesKey, vaultKey));
  const vaultBlob = await encryptVaultPlaintext(vaultKey, plaintext);

  await api.updateVaultBlob(vaultBlob, nextVersion);
  await api.updateVkWrap(vkWrapPassphrase);
  setStoredVaultVersion(userId, nextVersion);

  return {
    vaultKey,
    userPrivateKey: decodeBase64Url(plaintext.userPriv),
    plaintext,
  };
}
